import { useMemo } from 'react';
import { FileIcon } from 'lucide-react';
import { parseFormBody, isFormUrlEncoded } from '@mockd/shared/utils';
import type { ParsedBody, FileMetadata } from '@mockd/shared/types/parsed-body';
import { SYNTAX_CLASS } from '../../utils/syntaxColors';
import { JsonViewer } from './JsonViewer';

interface BodyViewerProps {
  body: string | null;
  contentType?: string;
  parsedBody?: ParsedBody | string | null;
}

function FieldTable({ fields }: { fields: Record<string, string> }) {
  return (
    <div className="code-surface overflow-x-auto">
      <table className="table table-xs font-mono">
        <thead>
          <tr>
            <th className={SYNTAX_CLASS.key}>Key</th>
            <th className={SYNTAX_CLASS.string}>Value</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(fields).map(([key, value]) => (
            <tr key={key}>
              <td className={SYNTAX_CLASS.key}>{key}</td>
              <td className={SYNTAX_CLASS.string}>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FileCards({ files }: { files: FileMetadata[] }) {
  return (
    <div className="space-y-2">
      {files.map((file, i) => (
        <div key={i} className="code-surface p-3 font-mono text-sm">
          <div className="flex items-center gap-2 mb-1">
            <FileIcon className="size-4 text-base-content/70" />
            <span className="font-semibold">{file.fieldName}</span>
          </div>
          <div className="text-xs text-base-content/70 space-y-0.5 ml-6">
            <div>Filename: <span className={SYNTAX_CLASS.string}>{file.fileName}</span></div>
            <div>Type: <span className={SYNTAX_CLASS.key}>{file.contentType}</span></div>
            <div>Size: <span className={SYNTAX_CLASS.number}>{formatSize(file.size)}</span></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Renders a request body with format-aware display.
 * Uses ParsedBody when available for type-appropriate rendering,
 * falls back to legacy behavior for pre-migration logs.
 */
export function BodyViewer({ body, contentType, parsedBody }: BodyViewerProps) {
  // Parse the parsedBody if it's a JSON string
  const parsed = useMemo((): ParsedBody | null => {
    if (!parsedBody) return null;
    if (typeof parsedBody === 'string') {
      try {
        return JSON.parse(parsedBody) as ParsedBody;
      } catch {
        return null;
      }
    }
    return parsedBody;
  }, [parsedBody]);

  // If we have a ParsedBody, render based on type
  if (parsed) {
    switch (parsed.type) {
      case 'json':
        return parsed.raw ? <JsonViewer data={parsed.raw} /> : <span className="text-base-content/70 italic">No data</span>;

      case 'form':
        return Object.keys(parsed.fields).length > 0
          ? <FieldTable fields={parsed.fields} />
          : <span className="text-base-content/70 italic">No data</span>;

      case 'multipart':
        return (
          <div className="space-y-3">
            {Object.keys(parsed.fields).length > 0 && (
              <FieldTable fields={parsed.fields} />
            )}
            {parsed.files.length > 0 && (
              <FileCards files={parsed.files} />
            )}
            {Object.keys(parsed.fields).length === 0 && parsed.files.length === 0 && (
              <span className="text-base-content/70 italic">No data</span>
            )}
          </div>
        );

      case 'text':
        return parsed.raw
          ? <pre className="code-surface p-3 font-mono text-sm whitespace-pre-wrap break-all">{parsed.raw}</pre>
          : <span className="text-base-content/70 italic">No data</span>;

      case 'binary':
        return (
          <div className="code-surface p-3 text-sm text-base-content/70 italic">
            Binary content ({parsed.files.length > 0 ? `${parsed.files.length} file(s)` : 'no preview available'})
          </div>
        );
    }
  }

  // Legacy fallback: no parsed_body available
  return <LegacyBodyViewer body={body} contentType={contentType} />;
}

/**
 * Legacy body rendering for pre-migration logs where parsed_body is null.
 */
function LegacyBodyViewer({ body, contentType }: { body: string | null; contentType?: string }) {
  const formData = useMemo(() => {
    if (!body || !contentType || !isFormUrlEncoded(contentType)) return null;
    const parsed = parseFormBody(body);
    return Object.keys(parsed).length > 0 ? parsed : null;
  }, [body, contentType]);

  if (!body || body === 'null') {
    return <span className="text-base-content/70 italic">No data</span>;
  }

  if (formData) {
    return <FieldTable fields={formData} />;
  }

  return <JsonViewer data={body} />;
}
